<template>
  <a-page
    :image_source="image_source"
    :image_height="image_height"
    :color="isGroup ? '#ffffff' : '#f1f1f1'"
  >
    <template v-slot:header_options>
      <a-btn-pdf
        v-if="has_permission('export_pdf')"
        :config="pdf_config"
        class="mr-4"
      ></a-btn-pdf>
      <a-rms-range-selector
        v-if="!isGroup"
        ref="a-rms-range-selector"
        :event_period="[strategy_from, strategy_to]"
        @updatePeriod="updateRange"
        class="mr-2"
      >
      </a-rms-range-selector>
      <a-rms-import-dates
        ref="rms_import_dates"
        v-if="!isGroup"
      ></a-rms-import-dates>
    </template>
    <a-hotel-selector-rms
      ref="a-hotel-selector-rms"
      v-if="isGroup"
    ></a-hotel-selector-rms>
    <rms-strategy-axel-app
      v-else
      ref="rms-strategy-app"
      :event_period="[strategy_from, strategy_to]"
    ></rms-strategy-axel-app>
  </a-page>
</template>

<script>
import aBtnPdf from "@/components/aBtnPdf.vue";
import aPage from "@/components/aPage";
import rmsStrategyAxelApp from "@/components/app/axel/rmsStrategyApp";
import aRmsImportDates from "@/components/rms/aRmsImportDates";
import aRmsRangeSelector from "@/components/rms/aRmsRangeSelector";
import aHotelSelectorRms from "@/components/selector/aHotelSelectorRms";
import Vue from "vue";
import { mapGetters, mapState } from "vuex";

export default {
  name: "rms-strategy",
  components: {
    aBtnPdf,
    aPage,
    rmsStrategyAxelApp,
    aRmsRangeSelector,
    aHotelSelectorRms,
    aRmsImportDates
  },
  data: () => ({
    image_source: "/img/header.jpg" //Mandatory path
  }),
  methods: {
    updateRange(v) {
      let range = JSON.parse(JSON.stringify(v));
      this.strategy_from = range[0];
      this.strategy_to = range[1];

      if (!this.current_user.settings) {
        this.$set(this.current_user, "settings", {});
      }
      if (!this.current_user.settings["rms_strategy"]) {
        this.$set(this.current_user.settings, "rms_strategy", {});
      }
      if (!this.current_user.settings["rms_strategy"]["0"]) {
        this.$set(this.current_user.settings["rms_strategy"], "0", {});
      }

      this.current_user.settings["rms_strategy"]["0"]["strategy_from"] =
        range[0];
      this.current_user.settings["rms_strategy"]["0"]["strategy_to"] = range[1];
      this.saveUserSettings(this.current_user.settings["rms_strategy"]["0"]);
    },
    getFriendlyDate(date) {
      return Vue.moment(date).format("DD/MM/YYYY");
    },
    saveUserSettings(settings) {
      this.$store.dispatch("setRmsStrategyUserSettings", {
        settings: settings,
        hotel_id: "0"
      });
    },
    setInitDates() {
      // If dates are explicitly forced via query params (e.g. entering from the
      // dashboard "see strategy" button), honour them over the saved settings.
      if (
        this.query_params &&
        this.query_params.from_date &&
        this.query_params.to_date
      ) {
        this.strategy_from = this.query_params.from_date;
        this.strategy_to = this.query_params.to_date;

        // Persist the forced selection as the user's saved setting so it stays
        // consistent. Only possible once the user is loaded.
        if (this.current_user) {
          if (!this.current_user.settings) {
            this.$set(this.current_user, "settings", {});
          }
          if (!this.current_user.settings["rms_strategy"]) {
            this.$set(this.current_user.settings, "rms_strategy", {});
          }
          if (!this.current_user.settings["rms_strategy"]["0"]) {
            this.$set(this.current_user.settings["rms_strategy"], "0", {});
          }
          this.current_user.settings["rms_strategy"]["0"][
            "strategy_from"
          ] = this.query_params.from_date;
          this.current_user.settings["rms_strategy"]["0"][
            "strategy_to"
          ] = this.query_params.to_date;
          this.saveUserSettings(
            this.current_user.settings["rms_strategy"]["0"]
          );

          // Consume the query params so a later range change (or current_user
          // update) isn't reverted back to the forced month.
          const query = { ...this.$route.query };
          delete query.from_date;
          delete query.to_date;
          this.$router.replace({ query }).catch(() => {});
        }
        return;
      }

      // Initialize selector
      if (
        this.current_user &&
        this.current_user.settings &&
        this.current_user.settings["rms_strategy"] &&
        this.current_user.settings["rms_strategy"]["0"] &&
        this.current_user.settings["rms_strategy"]["0"]["strategy_from"] &&
        this.current_user.settings["rms_strategy"]["0"]["strategy_to"]
      ) {
        this.strategy_from = this.current_user.settings["rms_strategy"]["0"][
          "strategy_from"
        ];
        this.strategy_to = this.current_user.settings["rms_strategy"]["0"][
          "strategy_to"
        ];
      }
      if (!this.strategy_from) {
        this.strategy_from = Vue.moment()
          .startOf("month")
          .format("YYYY-MM-DD");
      }
      if (!this.strategy_to) {
        this.strategy_to = Vue.moment()
          .endOf("month")
          .format("YYYY-MM-DD");
      }
    }
  },
  computed: {
    isGroup() {
      return this.current_hotel && this.current_hotel.children_number > 0;
    },
    image_height() {
      return !this.isGroup ? 90 : 200;
    },
    strategy_from: {
      get() {
        return this.$store.state.rms.strategy_from;
      },
      set(value) {
        this.$store.dispatch("setStrategyFrom", value);
      }
    },
    strategy_to: {
      get() {
        return this.$store.state.rms.strategy_to;
      },
      set(value) {
        this.$store.dispatch("setStrategyTo", value);
      }
    },
    pdf_config() {
      return {
        filename:
          this.$t("revenue_strategy") +
          " - " +
          this.current_hotel.name +
          " -- " +
          this.getFriendlyDate(this.strategy_from) +
          " - " +
          this.getFriendlyDate(this.strategy_to),
        elements: [
          {
            id: "rms-strategy-table",
            is_table: true
          }
        ],
        hotel_name: this.current_hotel.name,
        section_name: this.$t("revenue_strategy")
      };
    },
    ...mapState({
      available_permissions: state => state.user.available_permissions,
      query_params: state => state.route.query
    }),
    ...mapGetters({
      current_hotel: "current_hotel",
      current_user: "current_user",
      has_permission: "has_permission"
    })
  },
  mounted() {
    this.setInitDates();
  },
  watch: {
    current_user: {
      handler(newVal) {
        this.setInitDates();
      }
    }
  }
};
</script>
